import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJobStore } from '@core/job/useJobStore';
import { useSiteStore } from '@core/site/useSiteStore';
import { useJobsTable } from '@/modules/jobs/hooks/useJobsTable';
import JobsTable from '@/modules/jobs/components/JobsTable';
import { JobFilters } from '@/modules/jobs/components/JobFilters';

export const ClientJobs = ({ clientId }: { clientId: string }) => {
  const navigate = useNavigate();
  const getJobsByClientId = useJobStore((state) => state.getJobsByClientId);
  const { fetchAllJobsForClient } = useJobStore();
  const { fetchSitesForClient, getSitesForClient } = useSiteStore();
  const jobs = getJobsByClientId(clientId);
  const sites = getSitesForClient(clientId);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<
    string | undefined
  >();
  const [hasMore, setHasMore] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const hasFetched = useRef(false);

  const {
    sortField,
    sortDirection,
    setSearchQuery,
    setStatusFilters,
    setSiteFilters,
    handleSort,
    paginatedFilteredJobs,
    pagination,
  } = useJobsTable(jobs);

  const fetchData = useCallback(
    async (isNewPage: boolean = false) => {
      if (jobs.length === 0) {
        setIsFetching(true);
      }
      const response = await fetchAllJobsForClient(
        clientId,
        itemsPerPage,
        isNewPage ? lastEvaluatedKey : undefined,
      );
      setHasMore(response.hasMore);
      setLastEvaluatedKey(response.lastEvaluatedKey || undefined);
      setIsFetching(false);
    },
    [
      clientId,
      itemsPerPage,
      fetchAllJobsForClient,
      lastEvaluatedKey,
      jobs?.length,
    ],
  );

  useEffect(() => {
    fetchSitesForClient(clientId);
  }, [clientId, fetchSitesForClient]);

  useEffect(() => {
    if (
      !hasFetched.current ||
      (pagination.currentPage > pagination.totalPages && hasMore)
    ) {
      fetchData(hasMore).then(() => {
        hasFetched.current = true;
      });
    }
  }, [
    clientId,
    fetchData,
    pagination.currentPage,
    pagination.totalPages,
    hasMore,
  ]);

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setLastEvaluatedKey(undefined);
  };

  const handleJobClick = (jobId: string) => {
    navigate(`/jobs/${jobId}`);
  };

  return (
    <div className="space-y-4">
      <JobFilters
        jobs={jobs}
        sites={sites}
        onSearch={setSearchQuery}
        onStatusFiltersChange={setStatusFilters}
        onSiteFiltersChange={setSiteFilters}
      />
      <JobsTable
        jobs={paginatedFilteredJobs}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        onJobClick={handleJobClick}
        showClientColumn={!clientId}
        pagination={{
          ...pagination,
          itemsPerPage,
          hasMore,
          onItemsPerPageChange: handleItemsPerPageChange,
        }}
        isLoading={isFetching}
      />
    </div>
  );
};

export default ClientJobs;
